import React from 'react';
import { useLocalize } from 'localize-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { JourneyStatusType, VehicleType, VehicleTypeDisplayName } from '@transferz/fe-models';
import { Card } from '@transferz/fe-component-library/src/components/card/Card';
import { Button } from '@transferz/fe-component-library/src/components/button/Button';
import Typography from '@transferz/fe-component-library/src/components/typography/Typography';
import User from '@transferz/fe-component-library/src/components/icons/UserIcon';
import Car from '@transferz/fe-component-library/src/components/icons/CarIcon';
import Assign from '@transferz/fe-component-library/src/components/icons/AssignIcon';
import More from '@transferz/fe-component-library/src/components/icons/MoreIcon';
import SandClock from '@transferz/fe-component-library/src/components/icons/SandClock';
import PhoneRing from '@transferz/fe-component-library/src/components/icons/PhoneRingIcon';
import Profile from '@transferz/fe-component-library/src/components/icons/ProfileIcon';
import { ExtendedCard } from '../../../../components/card/ExtendedCard';
import { GeoPoint } from '../../../../components/geoPoint/GeoPoint';
import { getDateDifference } from '../../../../utils/utils';
import '../../journey.scss';
import './driverDetails.scss';

export const DriverDetails: React.FC<any> = ({ journey }) => {
  const { translate } = useLocalize();
  const flags = useFlags();
  const isCancelled = journey.status === JourneyStatusType.CancelledFree || journey.status === JourneyStatusType.CancelledWithCosts;
  const showInfoText = journey.status === JourneyStatusType.Pending || journey.status === JourneyStatusType.Confirmed || journey.status === JourneyStatusType.Planned;
  const driverPhotoExternalKey = journey?.driver?.photoFileExternalKey;
  const driverPhoto = driverPhotoExternalKey ? `${window.tz_globalConfigs.storageFilesDownloadUrl}/download?externalKey=${driverPhotoExternalKey}` : '';
  const lastKnownDriverPositionUpdated = journey?.journeyExecutionInfo?.lastKnownDriverPositionUpdated;
  const lastKnownDriverPositionInMinutes = getDateDifference(lastKnownDriverPositionUpdated);
  const isFAQCompleted = journey.status === JourneyStatusType.JourneyInProgress
    || journey.status === JourneyStatusType.Completed
    || journey.status === JourneyStatusType.CancelledFree
    || journey.status === JourneyStatusType.CancelledWithCosts;
  const isCompleted = journey.status === JourneyStatusType.Completed;
  return (
    <div className={`driver-details ${flags?.showNewDesign ? 'card-wrap' : ''} ${journey?.driver?.name ? 'driver' : ''} ${isFAQCompleted ? 'faq-completed' : ''} ${isCompleted ? 'completed' : ''}`}>
      {flags?.showNewDesign
        ? isCancelled
          ? null
          : (
            <Card
              title={translate('yourDriver')}
              onCollapse={() => { }}
              collapsed={false}
              isGrey={showInfoText}
              collapsibleIcon={false}
            >
              {showInfoText
                ? (
                  <div className="d-flex mb-24">
                    <SandClock />
                    <Typography variant="body1">{translate('driverInfoLater')}</Typography>
                  </div>
                )
                : (
                  <>
                    {(journey?.driver?.mobilePhoneNumber || journey?.driver?.name) && (
                      <div className="d-flex mb-24">
                        <User />
                        <Typography variant="body1">
                          {journey?.driver?.name || translate('b_unknownDriver')}
                        </Typography>
                        {driverPhoto
                          ? (
                            <div className="image-container">
                              <img src={driverPhoto} />
                            </div>
                          )
                          : (
                            <div className="image-container placeholder">
                              <Profile width={50} />
                            </div>
                          )
                        }
                      </div>
                    )}
                    {journey?.vehicle?.category && (
                      <div className="d-flex mb-24">
                        <Car />
                        <Typography variant="body1">
                          {VehicleTypeDisplayName[journey?.vehicle?.category as VehicleType]}
                        </Typography>
                      </div>
                    )}
                    {journey?.vehicle?.makeName && journey?.vehicle?.modelName && (
                      <div className="d-flex mb-24">
                        <Car />
                        <Typography variant="body1">
                          {journey?.vehicle?.makeName} {journey?.vehicle?.modelName}
                        </Typography>
                      </div>
                    )}
                    {journey?.vehicle?.licensePlate && (
                      <div className="d-flex mb-24">
                        <More height="20" />
                        <Typography variant="body1">
                          {journey?.vehicle?.licensePlate}
                        </Typography>
                      </div>
                    )}
                    {(journey?.status === JourneyStatusType.DriverArrived
                      || journey?.status === JourneyStatusType.DriverUnderway
                      || journey?.status === JourneyStatusType.JourneyInProgress)
                      && journey?.journeyExecutionInfo?.lastKnownDriverPosition?.lat
                      ? (
                        <>
                          <div className="d-flex mb-24">
                            <Assign height="20" />
                            <Typography variant="body1">
                              {lastKnownDriverPositionUpdated
                                ? `${translate('lastknownLocation')} ( ${lastKnownDriverPositionInMinutes} ${translate(lastKnownDriverPositionInMinutes > 1 ? 'minutesAgo' : 'minAgo')} )`
                                : translate('lastknownLocation')
                              }
                            </Typography>
                          </div>
                          <div className="mb-24">
                            <GeoPoint
                              position={journey?.journeyExecutionInfo?.lastKnownDriverPosition}
                              center={journey?.journeyExecutionInfo?.lastKnownDriverPosition}
                              name="lastknownDriverLocation"
                              validationMsg={''}
                              draggable={false}
                              zoom={17}
                            />
                          </div>
                        </>
                      )
                      : null
                    }
                    {journey?.driver?.mobilePhoneNumber && (
                      <div className="d-flex">
                        <a href={`tel:${journey?.driver?.mobilePhoneNumber}`} className="call-link">
                          <Button
                            priority="primary"
                            variant="info"
                            label={translate('callDriver')}
                            icon={<PhoneRing />}
                            iconPosition="left"
                            size="lg"
                          />
                        </a>
                      </div>
                    )}
                  </>
                )
              }
            </Card>
          )
        : (
          <ExtendedCard title={translate('driverDetailsTitle')} isEditable={true}>
            {journey?.driver ? (
              <div className="mb-24">
                <div className="text-bold">{translate('driver')}</div>
                {journey?.driver?.mobilePhoneNumber && <div className="text-normal">{journey?.driver?.name || translate('b_unknownDriver')}</div>}
                <div className="text-normal">{journey?.driver?.mobilePhoneNumber}</div>
                <div className="text-normal">{journey?.vehicle?.licensePlate}</div>
              </div>
            ) : (journey?.transferCompany?.organization?.name)
              ? (
                <div className="mb-24">
                  <div className="text-bold">{translate('driver')}</div>
                  <div className="text-normal">{translate('noDriver')}</div>
                </div>
              )
              : (
                <div className="text-normal">{translate('noTransferCompany')}</div>
              )
            }
            {journey?.transferCompany?.organization?.name && (
              <div className="mb-24">
                <div className="text-bold">{translate('transferCompany')}</div>
                <div className="text-normal">{journey?.transferCompany?.organization?.name}</div>
                <div className="text-normal">{journey?.transferCompany?.phone}</div>
              </div>
            )}
          </ExtendedCard>
        )
      }
    </div>
  );
};
